import React, { useState } from 'react';
import { validarArchivos, validarCfClp, validarCfUsd, generarSeguimientoComercial, insertarCfClp, insertarCfUsd } from '../api/apiConfig';
import { useUser } from '../context/UserContext';
import { MdEdit, MdAdd, MdSave } from 'react-icons/md';

const CrearReporteDiario = () => {
  const { user } = useUser();
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [data, setData] = useState(null);
  const [cfClpData, setCfClpData] = useState(null);
  const [cfUsdData, setCfUsdData] = useState(null);
  const [error, setError] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editingUsdRow, setEditingUsdRow] = useState(null);
  const [editUsdValues, setEditUsdValues] = useState({});

  const handleSubmit = async () => {
    if (!selectedDate) {
      setError('Por favor selecciona una fecha');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Llamar a la primera API
      const result1 = await validarArchivos(selectedDate);
      setData(result1);
      
      // Intentar llamar a la segunda API
      try {
        const result2 = await validarCfClp(selectedDate);
        setCfClpData(result2);
      } catch (cfClpError) {
        console.error('Error en validarCfClp:', cfClpError);
      }
      
      // Intentar llamar a la tercera API
      try {
        const result3 = await validarCfUsd(selectedDate);
        setCfUsdData(result3);
      } catch (cfUsdError) {
        console.error('Error en validarCfUsd:', cfUsdError);
        setCfUsdData([]);
      }
    } catch (err) {
      setError('Error al consultar la API: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportMessage('');
    
    try {
      const result = await generarSeguimientoComercial(selectedDate);
      const started = result?.status === 'started';
      setReportMessage(
        result?.message ||
          (started
            ? 'El seguimiento comercial se está generando. Puede tardar 1-2 minutos; después recargá la fecha para verlo.'
            : 'Seguimiento comercial generado exitosamente')
      );
    } catch (err) {
      setReportMessage('Error al generar seguimiento comercial: ' + err.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleEdit = (index, item) => {
    setEditingRow(index);
    setEditValues({
      COSTO_FONDO: item.COSTO_FONDO === null ? '' : item.COSTO_FONDO,
      PRIMA_RIE: item.PRIMA_RIE === null ? '' : item.PRIMA_RIE
    });
  };

  const handleSave = async (index, item) => {
    try {
      await insertarCfClp(
        item.fecha,
        parseFloat(editValues.COSTO_FONDO) || 0,
        parseFloat(editValues.PRIMA_RIE) || 0,
        user?.email || ''
      );
      
      // Actualizar los datos localmente
      const updatedData = [...cfClpData.data];
      updatedData[index] = {
        ...updatedData[index],
        COSTO_FONDO: parseFloat(editValues.COSTO_FONDO) || 0,
        PRIMA_RIE: parseFloat(editValues.PRIMA_RIE) || 0
      };
      setCfClpData({ ...cfClpData, data: updatedData });
      
      setEditingRow(null);
      setEditValues({});
    } catch (err) {
      console.error('Error al guardar:', err);
    }
  };

  const handleEditUsd = (index, item) => {
    setEditingUsdRow(index);
    setEditUsdValues({
      COSTO_FONDO: item.COSTO_FONDO === null ? '' : item.COSTO_FONDO,
      PRIMA_RIE: item.PRIMA_RIE === null ? '' : item.PRIMA_RIE
    });
  };

  const handleSaveUsd = async (index, item) => {
    try {
      await insertarCfUsd(
        item.NUM_OPE,
        parseFloat(editUsdValues.COSTO_FONDO) || 0,
        parseFloat(editUsdValues.PRIMA_RIE) || 0
      );
      
      // Actualizar los datos localmente
      const updatedData = [...cfUsdData];
      updatedData[index] = {
        ...updatedData[index],
        COSTO_FONDO: parseFloat(editUsdValues.COSTO_FONDO) || 0,
        PRIMA_RIE: parseFloat(editUsdValues.PRIMA_RIE) || 0
      };
      setCfUsdData(updatedData);
      
      setEditingUsdRow(null);
      setEditUsdValues({});
    } catch (err) {
      console.error('Error al guardar USD:', err);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Crear Reporte Diario</h1>
        <p className="text-gray-600">Validación de archivos y seguimiento comercial</p>
      </div>
      
      <div className="seccion-datos mb-6">
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Fecha
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary mt-6"
          >
            {loading ? 'Consultando...' : 'Validar Archivos'}
          </button>
        </div>
        
        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
      </div>

      {data && (
        <div className="seccion-datos">
          <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>Resultados de Validación</h3>
          
          <div className="mb-6">
            <p className="text-green-600 font-medium">{data.mensaje}</p>
            <p className="text-sm text-gray-600">Fecha de validación: {new Date(data.fecha_validacion).toLocaleString()}</p>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Archivos</p>
              <p className="text-2xl font-bold text-blue-600">{data.resumen.total_archivos}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Encontrados</p>
              <p className="text-2xl font-bold text-green-600">{data.resumen.archivos_encontrados}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Faltantes</p>
              <p className="text-2xl font-bold text-red-600">{data.resumen.archivos_faltantes}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Completitud</p>
              <p className="text-2xl font-bold text-purple-600">{data.resumen.porcentaje_completitud}%</p>
            </div>
          </div>

          <div className="tabla-container">
            <table>
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Nombre</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.detalle_archivos.map((archivo, index) => (
                  <tr key={index}>
                    <td>{archivo.archivo}</td>
                    <td>{archivo.nombre_archivo}</td>
                    <td>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        archivo.existe 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {archivo.existe ? 'Existe' : 'Faltante'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {cfClpData && (
        <div className="seccion-datos mt-6">
          <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>Validación CF CLP</h3>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600">Año: {cfClpData.anio} | Mes: {cfClpData.mes} | Total días hábiles: {cfClpData.total_dias_habiles}</p>
          </div>

          <div className="tabla-container">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Costo Fondo</th>
                  <th>Prima Rie</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cfClpData.data.map((item, index) => (
                  <tr key={index} className={item.COSTO_FONDO === 0 ? 'bg-red-50' : ''}>
                    <td>{item.fecha.split('-').reverse().join('/')}</td>
                    <td>
                      {editingRow === index ? (
                        <input
                          type="number"
                          step="0.001"
                          value={editValues.COSTO_FONDO}
                          onChange={(e) => setEditValues({...editValues, COSTO_FONDO: e.target.value})}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        item.COSTO_FONDO === null || item.COSTO_FONDO === undefined ? 'N/A' : item.COSTO_FONDO
                      )}
                    </td>
                    <td>
                      {editingRow === index ? (
                        <input
                          type="number"
                          step="0.001"
                          value={editValues.PRIMA_RIE}
                          onChange={(e) => setEditValues({...editValues, PRIMA_RIE: e.target.value})}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        item.PRIMA_RIE === null || item.PRIMA_RIE === undefined ? 'N/A' : item.PRIMA_RIE
                      )}
                    </td>
                    <td style={{textAlign: 'center'}}>
                      {editingRow === index ? (
                        <button
                          onClick={() => handleSave(index, item)}
                          className="p-2 border border-gray-300 text-green-600 hover:bg-green-50 hover:text-green-800"
                        >
                          <MdSave size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEdit(index, item)}
                          className="p-2 border border-gray-300 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                        >
                          {(item.COSTO_FONDO === null || item.COSTO_FONDO === undefined || item.COSTO_FONDO === 0) ? 
                            <MdAdd size={16} /> : <MdEdit size={16} />
                          }
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(cfUsdData !== null) && (
        <div className="seccion-datos mt-6">
          <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>Validación CF USD</h3>

          <div className="tabla-container">
            <table>
              <thead>
                <tr>
                  <th>Número Operación</th>
                  <th>Costo Fondo</th>
                  <th>Prima Rie</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cfUsdData && cfUsdData.length > 0 ? (
                  cfUsdData.map((item, index) => (
                    <tr key={index} className={item.COSTO_FONDO === 0 ? 'bg-red-50' : ''}>
                      <td>{item.NUM_OPE}</td>
                      <td>
                        {editingUsdRow === index ? (
                          <input
                            type="number"
                            step="0.001"
                            value={editUsdValues.COSTO_FONDO}
                            onChange={(e) => setEditUsdValues({...editUsdValues, COSTO_FONDO: e.target.value})}
                            className="w-full px-2 py-1 border rounded"
                          />
                        ) : (
                          item.COSTO_FONDO === null || item.COSTO_FONDO === undefined ? 'N/A' : item.COSTO_FONDO
                        )}
                      </td>
                      <td>
                        {editingUsdRow === index ? (
                          <input
                            type="number"
                            step="0.001"
                            value={editUsdValues.PRIMA_RIE}
                            onChange={(e) => setEditUsdValues({...editUsdValues, PRIMA_RIE: e.target.value})}
                            className="w-full px-2 py-1 border rounded"
                          />
                        ) : (
                          item.PRIMA_RIE === null || item.PRIMA_RIE === undefined ? 'N/A' : item.PRIMA_RIE
                        )}
                      </td>
                      <td style={{textAlign: 'center'}}>
                        {editingUsdRow === index ? (
                          <button
                            onClick={() => handleSaveUsd(index, item)}
                            className="p-2 border border-gray-300 text-green-600 hover:bg-green-50 hover:text-green-800"
                          >
                            <MdSave size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleEditUsd(index, item)}
                            className="p-2 border border-gray-300 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                          >
                            {(item.COSTO_FONDO === null || item.COSTO_FONDO === undefined || item.COSTO_FONDO === 0) ? 
                              <MdAdd size={16} /> : <MdEdit size={16} />
                            }
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{textAlign: 'center', color: 'var(--if-text-muted)'}}>
                      No hay datos disponibles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && (
        <div className="seccion-datos mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{color: 'var(--if-primary)', margin: '0', fontSize: '1.2rem', fontWeight: '600'}}>Generar Seguimiento</h3>
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport || !selectedDate}
              className="btn-primary"
            >
              {generatingReport ? 'Generando...' : 'Generar Seguimiento Comercial'}
            </button>
          </div>
          
          {reportMessage && (
            <div className={`mt-4 p-3 rounded ${
              reportMessage.includes('Error')
                ? 'bg-red-100 border border-red-400 text-red-700'
                : reportMessage.includes('está generando')
                  ? 'bg-amber-100 border border-amber-400 text-amber-800'
                  : 'bg-green-100 border border-green-400 text-green-700'
            }`}>
              {reportMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CrearReporteDiario;